import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { FileItem } from 'ng2-file-upload';
import { Observable, forkJoin, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MapComponent, MapOptions } from '../../components/map/map.component';
import { I18nTranslator } from '../../i18n/translate';
import { Campaign } from '../../models/campaign.model';
import { Farm } from '../../models/farm.model';
import { Polygon } from '../../models/geometry.model';
import {
  CerealOutcome,
  Country,
  Destination,
  FallowAge,
  FallowType,
  ListItem,
  PreviousCrop,
  Soil,
  SoilDepth,
  Specy,
  Variety,
} from '../../models/lists.model';
import { Page } from '../../models/page.model';
import { Parcel } from '../../models/parcel.model';
import { CampaignYearPipe } from '../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { HectareConverterPipe } from '../../pipes/hectareConverter.pipe';
import { VarietySpecyPipe } from '../../pipes/varietySpecy.pipe';
import { CampaignService } from '../../services/campaign.service';
import { FarmService } from '../../services/farm.service';
import { FieldviewService } from '../../services/fieldview.service';
import { I18nService } from '../../services/i18n.service';
import { KeycloakService } from '../../services/keycloak.service';
import { ListType, ListsService } from '../../services/lists.service';
import { MapService } from '../../services/map.service';
import { ParcelService } from '../../services/parcel.service';
import { Tooltip } from '../../utils/Tooltip';
import { TooltipContent } from '../../utils/TooltipContent';
import { API_ENDPOINT } from '../../utils/env';
import { SHAPE_ENDPOINT } from '../../utils/env';
import { LayerJSON } from '../../utils/layerjson';
import { MultiFileUploader } from '../../utils/multi-file-upload.model';
import { PreventDoubleClickDirective } from '../../utils/preventDoubleClick';
import { correctDate, showErrorMessage, sortingNumber, sortingString } from '../../utils/utils';
import { PostalCodeValidator } from '../../utils/postal-code-validation';
import { FieldViewDialogComponent, FieldviewDialogContext } from './fieldview-dialog.component';
import { ParcelStatus } from '../../models/parcel.model';

// TODO plus utilisé ? uniquement KML
import omnivore from 'leaflet-omnivore';
import { TranslatePipe, TranslateWithPunctuationPipe, TranslateDatePipe } from '../../pipes/translate.pipe';
import { SafePipe } from '../../pipes/safe.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FileUploadModule } from 'ng2-file-upload';
class ShapefileUploader extends MultiFileUploader {
  static ALLOWED_FILE_TYPES = ['dbf', 'prj', 'shp', 'shx', 'fix'];
  public parcel: Parcel;

  constructor(
    private drawnItems: L.FeatureGroup,
    private map: L.Map,
    private modal: ModalService,
  ) {
    super({
      url: SHAPE_ENDPOINT + '/converter/shpFilesToGeom',
      autoUpload: true,
    });
  }

  onBeforeUploadItem() {
    const wrongFiles = this.queue
      .filter((item: FileItem) => {
        console.log(item.file.name);
        const fileExt = item.file.name.slice(item.file.name.lastIndexOf('.') + 1).toLowerCase();
        return ShapefileUploader.ALLOWED_FILE_TYPES.indexOf(fileExt) === -1;
      })
      .map((item: FileItem) => item.file.name);
    if (wrongFiles.length) {
      const errorMsg =
        wrongFiles.length > 1
          ? `Fichiers invalides : "${wrongFiles.join('", "')}".`
          : `Fichier invalide : "${wrongFiles.join(', ')}".`;
      showErrorMessage(
        this.modal,
        errorMsg +
          `\nLes fichiers doivent avoir les formats suivants : ${ShapefileUploader.ALLOWED_FILE_TYPES.join(', ')}`,
      );
    }
    // TODO progress spinner
  }

  onCompleteItem(item: any, response: any, status: any, headers: any) {
    this.queue = [];
    const geom = (JSON.parse(response) as Polygon[])[0];
    const poly = new L.Polygon(<any>geom.coordinates[0]);
    const oldLayer = this.drawnItems.getLayers()[0];
    if (oldLayer) {
      this.drawnItems.removeLayer(oldLayer);
    }
    this.drawnItems.addLayer(poly);
    this.map.fitBounds(poly.getBounds());

    if (this.parcel) {
      this.parcel.geometry = poly.toGeoJSON().geometry as any;
    }
    // TODO hide progress spinner
  }

  onErrorItem(item: any, response: any, status: any, headers: any) {
    console.debug('onErrorItem', this.queue);
    this.queue = [];
    showErrorMessage(this.modal, `Les fichiers envoyés ne semblent pas être au format valide.`);
    // TODO hide progress spinner
  }
}

@Component({
  standalone: true,
  selector: 'parcel-edit',
  styleUrls: ['parcel-edit.component.scss'],
  templateUrl: 'parcel-edit.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, MapComponent, Tooltip, TooltipContent, PreventDoubleClickDirective, VarietySpecyPipe, HectareConverterPipe, CapitalizePipe, CampaignYearPipe, TranslatePipe, TranslateWithPunctuationPipe, TranslateDatePipe, SafePipe, CommonModule, FormsModule, BsDatepickerModule, TooltipModule, FileUploadModule],
  providers: [MapService, ParcelService, FarmService, FieldviewService],
})
export class ParcelEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() parcel: Parcel;
  @Output() close = new EventEmitter();
  private isFirstActivation = true;
  error: any;
  sub: any;
  edited = false; // true if edition
  imported = false;
  parcelId: number;
  campaignId: number;

  context: FieldviewDialogContext = new FieldviewDialogContext();
  fieldviewParcel: Parcel = null;
  fieldViewUserLink: boolean = false;

  controlLayer: any = null;
  farmLayer: any = null;

  @ViewChild('mapComponent')
  mapComponent: any;

  postalCodeElementRef: ElementRef;
  mapOptions: MapOptions;
  @ViewChild('postalCode')
  set postalCodeER(postalCode: ElementRef) {
    this.postalCodeElementRef = postalCode;
    this.setupSoilObservers();
  }

  countryElementRef: ElementRef;
  @ViewChild('country')
  set countryER(country: ElementRef) {
    this.countryElementRef = country;
    this.setupSoilObservers();
  }

  countries: Country[];
  species: Specy[];
  soils: Soil[];
  soilDepths: SoilDepth[];
  previousCrops: PreviousCrop[];
  fallowTypes: FallowType[];
  fallowAges: FallowAge[];
  cerealOutcomes: CerealOutcome[];
  varieties: Variety[];
  destinations: Destination[];
  farms: Farm[];
  campaigns: Campaign[];
  setupSoilObserversDone = false;

  // postal code validation
  postalCodeError: string = null;

  // crop field restriction for non-admin users with orders
  canEditCrop: boolean = true;

  // dépend de previousCulture
  isFallowDisabled = true;
  isCerealDisabled = true;
  // dépend de la culture
  isShowPreviousCrop = false;
  isShowSoilDepth = false;
  isShowSowedOn = false;

  isPartner = false;
  isAdmin: boolean;

  drawnItems: L.FeatureGroup = L.featureGroup();
  isSowedOnCollapsed: boolean = false;

  map: L.Map;
  // keycloak token may change with time, so need to re init a proper uploader each time.
  uploader: ShapefileUploader;

  constructor(
    private _elementRef: ElementRef,
    private mapService: MapService,
    private parcelService: ParcelService,
    private farmService: FarmService,
    private secureLink: FieldviewService,
    private campaignService: CampaignService,
    private listService: ListsService,
    private i18nTranslator: I18nTranslator,
    private i18nService: I18nService,
    private cdr: ChangeDetectorRef,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.mapOptions = {
      center: [this.mapService.DEFAULT_LAT, this.mapService.DEFAULT_LONG],
      zoom: {
        defaultValue: this.mapService.DEFAULT_ZOOM,
        max: this.mapService.DEFAULT_MAX_ZOOM,
      },
      draw: {
        controls: true,
      },
    };

    this.context.configObservable.subscribe((value) => {
      this.fieldviewParcel = value;
      if (this.fieldviewParcel.geometry) {
        // reverse car renvoyé en Lon, Lat mais Leaflet utilise Lat, Lon
        const poly = new L.Polygon(
          this.fieldviewParcel.geometry.coordinates.map((c) => c.map((latLng) => [latLng[1], latLng[0]])) as any,
        );
        this.drawnItems.addLayer(poly);
        this.map.fitBounds(poly.getBounds());
      }
      this.handleUpdateParcel(this.fieldviewParcel);
    });
  }

  goBack(savedParcel: Parcel = null) {
    this.close.emit(savedParcel);
    this.router.navigate(['/parcels']);
    return false;
  }

  getFieldviewData() {
    this.context.showClose = true;
    this.context.isBlocking = false;
    this.context.size = 'lg';
    this.context.fieldviewParcel = undefined;
    this.context.backdrop = 'static';

    this.modal.open(FieldViewDialogComponent, this.context);
  }

  // TODO ajouter un loader
  getLists() {
    const listTypes = [
      ListType.COUNTRIES,
      ListType.SPECIES,
      ListType.SOIL_DEPTHS,
      ListType.PREVIOUS_CROPS,
      ListType.FALLOW_TYPES,
      ListType.FALLOW_AGES,
      ListType.CEREAL_OUTCOMES,
      ListType.VARIETIES,
      ListType.DESTINATIONS,
    ];
    return this.listService.getListTypes(ListType, ...listTypes);
  }

  handleLists(lists: Map<ListType, ListItem[]>) {
    this.countries = <Country[]>lists.get(ListType.COUNTRIES).sort(sortingString('label'));
    this.species = lists.get(ListType.SPECIES).sort(sortingString('label'));
    this.soilDepths = lists.get(ListType.SOIL_DEPTHS).sort(sortingNumber('id'));
    this.previousCrops = <PreviousCrop[]>lists.get(ListType.PREVIOUS_CROPS).sort(sortingString('label'));
    this.fallowTypes = lists.get(ListType.FALLOW_TYPES).sort(sortingString('label'));
    this.fallowAges = lists.get(ListType.FALLOW_AGES).sort(sortingNumber('id'));
    this.cerealOutcomes = lists.get(ListType.CEREAL_OUTCOMES).sort(sortingString('label'));
    this.varieties = <Variety[]>lists.get(ListType.VARIETIES).sort(sortingString('label'));
    this.destinations = <Destination[]>lists.get(ListType.DESTINATIONS).sort(sortingString('label'));
  }

  validatePostalCodeFormat() {
    const postalCode = this.parcel.postalCode;
    if (!postalCode) {
      this.postalCodeError = null;
      return;
    }

    const country = this.countries.find((c) => +this.parcel.country.id === c.id);
    if (!country) {
      this.postalCodeError = null;
      return;
    }

    this.postalCodeError = PostalCodeValidator.validate(postalCode, country.code);
  }

  checkCropEditPermission() {
    // Admin can edit everything including crop
    if (this.isAdmin) {
      this.canEditCrop = true;
      return;
    }

    // For other profiles, check if parcel has associated orders
    if (this.parcelId) {
      this.parcelService.getParcelStatuses([this.parcelId]).subscribe(
        (statuses: ParcelStatus[]) => {
          if (statuses && statuses.length > 0) {
            this.canEditCrop = this.canEditCropBasedOnStatus(statuses[0]);
          } else {
            this.canEditCrop = true;
          }
        },
        (error) => {
          // If error, allow editing by default
          this.canEditCrop = true;
        },
      );
    } else {
      // New parcel, allow editing
      this.canEditCrop = true;
    }
  }

  canEditCropBasedOnStatus(status: ParcelStatus): boolean {
    if (status && status.toolStatuses) {
      for (const toolKey in status.toolStatuses) {
        const toolStatuses: any = status.toolStatuses;
        const toolStatus = toolStatuses[toolKey];
        if (toolStatus.hasOrder) {
          return false;
        }
      }
    }
    return true;
  }

  getSoils() {
    const postalCode = this.parcel.postalCode;
    if (!postalCode) {
      return;
    }
    const country = this.countries.find((c) => +this.parcel.country.id === c.id);
    if (!country) {
      return;
    }
    const countryCode = country.code;

    // Validate postal code format
    this.validatePostalCodeFormat();
    if (this.postalCodeError) {
      return;
    }

    this.listService.getSoils(countryCode, postalCode).subscribe((soils) => {
      this.soils = soils.sort(sortingString('label'));
      if (this.parcel.soil.id) {
        // reset le soil de la parcelle si besoin
        const soil = soils.find((soil) => soil.id === +this.parcel.soil.id);
        if (!soil) {
          this.parcel.soil.id = null;
        }
      }
    });
  }

  private readKml(content: any) {
    const layer = omnivore.kml.parse(content);
    if (layer._layers.length > 1) {
    }
    let polygon: any;
    for (const i in layer._layers) {
      if (polygon) {
        showErrorMessage(this.modal, `Format de KML non pris en charge.`);
        return;
      }
      polygon = layer._layers[i];
    }
    polygon.addTo(this.map);
    const oldLayer = this.drawnItems.getLayers()[0];
    if (oldLayer) {
      this.drawnItems.removeLayer(oldLayer);
    }
    this.drawnItems.addLayer(polygon);
    this.map.fitBounds(polygon.getBounds());
  }

  private readSingleFile(e: any) {
    const fileName = e.target.files[0];
    console.log(fileName);
    if (!fileName) {
      return;
    }
    //omnivore.kml(fileName).addTo(this.map);
    const reader = new FileReader();
    reader.onload = (file) => {
      const contents: any = file.target;
      this.readKml(contents.result);
    };
    reader.readAsText(fileName);
  }

  save() {
    if (this.parcel.area < 10000) {
      showErrorMessage(
        this.modal,
        'Attention seules les parcelles avec une surface supérieure à 1 hectare peuvent être commandées.',
      );
      return;
    }
    // TODO vraie validation

    // traiter les champs optionnels

    let restorePreviousCrop = false;
    if (!this.parcel.previousCrop || !this.parcel.previousCrop.id) {
      this.parcel.previousCrop = null;
      restorePreviousCrop = true;
    }

    let restoreCerealOutcome = false;
    if (!this.parcel.cerealOutcome || !this.parcel.cerealOutcome.id) {
      this.parcel.cerealOutcome = null;
      restoreCerealOutcome = true;
    }

    let restoreFallowAge = false;
    if (!this.parcel.fallowAge || !this.parcel.fallowAge.id) {
      this.parcel.fallowAge = null;
      restoreFallowAge = true;
    }

    let restoreFallowType = false;
    if (!this.parcel.fallowType || !this.parcel.fallowType.id) {
      this.parcel.fallowType = null;
      restoreFallowType = true;
    }

    let restoreDestination = false;
    if (!this.parcel.destination || !this.parcel.destination.id) {
      this.parcel.destination = null;
      restoreDestination = true;
    }

    let restoreSoilDepth = false;
    if (!this.parcel.soilDepth || !this.parcel.soilDepth.id) {
      this.parcel.soilDepth = null;
      restoreSoilDepth = true;
    }

    if (this.parcel.sowedOn) {
      this.parcel.sowedOn = correctDate(<Date>this.parcel.sowedOn);
    }

    // TODO afficher loader
    this.parcelService.save(this.parcel).subscribe(
      (parcel) => {
        this.parcel = this.uploader.parcel = parcel;
        this.goBack(parcel);
      },
      (error) => {
        this.handleSaveError(error);
      },
    );

    if (restorePreviousCrop) {
      this.parcel.previousCrop = new PreviousCrop();
      this.parcel.previousCrop.id = null;
    }

    if (restoreCerealOutcome) {
      this.parcel.cerealOutcome = new CerealOutcome();
      this.parcel.cerealOutcome.id = null;
    }

    if (restoreFallowAge) {
      this.parcel.fallowAge = new FallowAge();
      this.parcel.fallowAge.id = null;
    }

    if (restoreFallowType) {
      this.parcel.fallowType = new FallowType();
      this.parcel.fallowType.id = null;
    }

    if (restoreSoilDepth) {
      this.parcel.soilDepth = new SoilDepth();
      this.parcel.soilDepth.id = null;
    }

    if (restoreDestination) {
      this.parcel.destination = new Destination();
      this.parcel.destination.id = null;
    }
  }

  getFarm() {
    if (this.farms == undefined) {
      this.farmService.getFarms().subscribe({
        next: (farm) => this.handleFarms(farm),
        error: (err) => console.log(err),
      });
    }
  }

  ngOnInit() {
    this.isUserLinkToFieldview();
  }

  newParcel() {
    // new sans détails
    this.parcel = this.uploader.parcel = new Parcel();
    this.parcel.country = new Country();
    // France par défaut
    this.parcel.country.id = 1;
    this.parcel.farm = new Farm();
    this.parcel.farm.id = null;
    this.parcel.specy = new Specy();
    this.parcel.specy.id = null;
    this.parcel.soil = new Soil();
    this.parcel.soil.id = null;
    this.parcel.soilDepth = new SoilDepth();
    this.parcel.soilDepth.id = null;
    this.parcel.previousCrop = new PreviousCrop();
    this.parcel.previousCrop.id = null;
    this.parcel.fallowType = new FallowType();
    this.parcel.fallowType.id = null;
    this.parcel.fallowAge = new FallowAge();
    this.parcel.fallowAge.id = null;
    this.parcel.cerealOutcome = new CerealOutcome();
    this.parcel.cerealOutcome.id = null;
    this.parcel.variety = new Variety();
    this.parcel.variety.id = null;
    this.parcel.destination = new Destination();
    this.parcel.destination.id = null;
    this.isSowedOnCollapsed = true;
    this.parcel.campaign = new Campaign();
    this.parcel.campaign.id = null;
    this.parcel.area = 0;
  }

  getParcel() {
    const obs1 = this.parcelService.getParcel(this.parcelId);
    const obs2 = this.campaignService.getCampaigns();
    const obs4 = this.getLists();
    const obs = forkJoin([obs1, obs2, obs4]).subscribe(
      (data: Array<any>) => {
        this.handleLists(data[2]); // besoin pour handleParcel
        this.handleParcel(data[0]);
        this.handleCampaigns(data[1]);
        this.getFarmParcel(this.parcel.farm.id, this.parcel.campaign.id);
        // a besoin de la parcelle et de la liste des pays
        this.getSoils();
      },
      (error) => {
        this.handleGetError(error);
      },
    );
  }

  fillDefaultSoildDepthValues() {
    if (!this.parcel.soilDepth.id) {
      const deepSoilDepth = this.soilDepths.find((x) => x.code === 'PROFOND');
      this.parcel.soilDepth.id = deepSoilDepth.id;
    }
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('parcel.edit.unauthorized'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('parcel.edit.error'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('parcel.edit.view.unauthorized')).then((r) => {
        this.router.navigate(['/parcels']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/parcels']);
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.context.configObservable.unsubscribe();
  }

  handleCampaigns(campaigns: Campaign[]) {
    this.campaigns = campaigns.sort(Campaign.compare).reverse();
    if (!this.edited) {
      if (this.campaignId) {
        this.parcel.campaign.id = this.campaignId;
      } else if (this.campaigns.length > 0) {
        this.parcel.campaign.id = this.campaigns[0].id;
      }
    }
  }

  handleFarms(farms: Farm[]) {
    this.farms = farms.sort((a, b) => a.name.localeCompare(b.name));
    if (!this.edited && !this.imported && this.farms.length === 1) {
      this.parcel.farm.id = this.farms[0].id;
      this.parcel.country.id = this.farms[0].country.id;
    }
  }

  handleUpdateParcel(parcel: Parcel) {
    this.parcel.name = parcel.name;
    this.parcel.area = parcel.area;
    this.parcel.geometry = parcel.geometry;
    this.parcel.location = parcel.location;
    this.cdr.detectChanges();
  }

  handleParcel(parcel: Parcel) {
    // TODO certain que ce handler est toujours
    // appelé après ngAfterViewInit ?
    // map doit être prêt

    if (this.imported) {
      this.newParcel();
      // copier les champs à importer
      this.parcel.name = parcel.name;
      this.parcel.postalCode = parcel.postalCode;
      this.parcel.country = parcel.country;
      this.parcel.pacCode = parcel.pacCode;
      this.parcel.geometry = parcel.geometry;
      this.parcel.farm = parcel.farm;
      this.parcel.soil = parcel.soil;
      this.parcel.soilDepth = parcel.soilDepth;
      // setter parcelle prec
      this.parcel.previousParcel = new Parcel();
      this.parcel.previousParcel.id = parcel.id;
    } else {
      this.parcel = this.uploader.parcel = parcel;
    }

    if (parcel.geometry) {
      // reverse car renvoyé en Lon, Lat mais Leaflet utilise Lat, Lon
      const poly = new L.Polygon(
        parcel.geometry.coordinates.map((c) => c.map((latLng) => [latLng[1], latLng[0]])) as any,
      );
      this.drawnItems.addLayer(poly);
      this.map.fitBounds(poly.getBounds());
    }

    if (!this.parcel.country || !this.parcel.country.id) {
      this.parcel.country = new Country();
      this.parcel.country.id = null;
    }
    if (!this.parcel.farm || !this.parcel.farm.id) {
      this.parcel.farm = new Farm();
      this.parcel.farm.id = null;
    }
    if (!this.parcel.specy || !this.parcel.specy.id) {
      this.parcel.specy = new Specy();
      this.parcel.specy.id = null;
    }
    if (!this.parcel.soil || !this.parcel.soil.id) {
      this.parcel.soil = new Soil();
      this.parcel.soil.id = null;
    }
    if (!this.parcel.soilDepth || !this.parcel.soilDepth.id) {
      this.parcel.soilDepth = new SoilDepth();
      this.parcel.soilDepth.id = null;
    }
    if (!this.parcel.previousCrop || !this.parcel.previousCrop.id) {
      this.parcel.previousCrop = new PreviousCrop();
      this.parcel.previousCrop.id = null;
    } else {
      if (this.parcel.previousCrop.fallow) {
        this.isFallowDisabled = false;
      } else if (this.parcel.previousCrop.cereal) {
        this.isCerealDisabled = false;
      }
    }
    if (!this.parcel.fallowType || !this.parcel.fallowType.id) {
      this.parcel.fallowType = new FallowType();
      this.parcel.fallowType.id = null;
    }
    if (!this.parcel.fallowAge || !this.parcel.fallowAge.id) {
      this.parcel.fallowAge = new FallowAge();
      this.parcel.fallowAge.id = null;
    }
    if (!this.parcel.cerealOutcome || !this.parcel.cerealOutcome.id) {
      this.parcel.cerealOutcome = new CerealOutcome();
      this.parcel.cerealOutcome.id = null;
    }
    if (!this.parcel.variety || !this.parcel.variety.id) {
      this.parcel.variety = new Variety();
      this.parcel.variety.id = null;
    }
    if (!this.parcel.campaign || !this.parcel.campaign.id) {
      this.parcel.campaign = new Campaign();
      this.parcel.campaign.id = null;
    }

    if (this.parcel.identifiantFms) {
      if (this.parcel.identifiantFms.startsWith('WIUZ:') || this.parcel.identifiantFms.startsWith('SMAG:')) {
        this.isPartner = true;
      }
    }

    this.updateFormForSpecy(this.parcel.specy.id);
    this.fillDefaultSoildDepthValues();

    // Validate postal code format if both country and postal code are present
    if (this.parcel.country && this.parcel.country.id && this.parcel.postalCode) {
      this.validatePostalCodeFormat();
    }
  }

  // Cette fonction
  handleParcelsFarm(parcels: Page<Parcel>) {
    if (!this.controlLayer) {
      this.controlLayer = new L.Control.Layers({}, {}, { collapsed: false, position: 'bottomright' }).addTo(this.map);
    }

    // on supprime les layer de l'exploitation pour etre sur qu'elle n'y est plus
    if (this.farmLayer) {
      this.controlLayer.removeLayer(this.farmLayer);
      this.map.removeLayer(this.farmLayer);
    }

    // Le feature group permet d'ajouter chaque parcelle comme un polyon indépendant
    // ce qui permet d'avoir des popup personalisé
    const farmParcels = L.featureGroup().addTo(this.map);
    this.map.addLayer(farmParcels);

    parcels.content.forEach((value) => {
      if (value.id !== this.parcelId && value.geometry) {
        const polygon = new L.Polygon(
          value.geometry.coordinates.map((c) => c.map((latLng) => [latLng[1], latLng[0]])) as any,
          {
            color: '#fc6800',
            weight: 4,
            opacity: 0.7,
            fillOpacity: 0.3,
            fillColor: '#fc6800',
          },
        )
          .bindPopup(value.name)
          .addTo(this.map);
        farmParcels.addLayer(polygon);
      }
    });
    this.farmLayer = farmParcels;
    this.controlLayer.addOverlay(
      this.farmLayer,
      '<b>' + this.i18nTranslator.translate('parcel.edit.map.showAutresParcelles') + '</b>',
    );
  }

  setupSoilObservers() {
    // TODO peut mieux faire ??
    if (!this.setupSoilObserversDone && this.postalCodeElementRef && this.countryElementRef) {
      const obs1 = fromEvent(this.postalCodeElementRef.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      const obs2 = fromEvent(this.countryElementRef.nativeElement, 'change');
      merge(obs1, obs2)
        .pipe(debounceTime(10))
        .subscribe((term: any) => {
          this.validatePostalCodeFormat();
          this.getSoils();
        });
      this.setupSoilObserversDone = true;
    }
  }

  ngAfterViewInit() {
    this.uploader = new ShapefileUploader(this.drawnItems, this.map, this.modal);
    this.sub = this.route.params.subscribe((params) => {
      if (this.route.snapshot.url[1].path === 'new') {
        this.edited = false;
        if (params['campaignId'] !== undefined) {
          // new
          this.campaignId = +params['campaignId'];
          if (params['parcelId'] !== undefined) {
            this.imported = true;
            this.parcelId = +params['parcelId'];
            // va aussi récupérer les listes
            this.getParcel();
          } else {
            this.newParcel();
            this.campaignService.getCampaigns().subscribe((x) => {
              this.handleCampaigns(x);
            });
            this.farmService.getFarms().subscribe((x) => {
              this.handleFarms(x);
              this.getFarmParcel(this.parcel.farm.id, this.parcel.campaign.id);
            });
            this.getLists().subscribe((lists: Map<ListType, ListItem[]>) => {
              this.handleLists(lists);
            });
          }
        } else {
          this.newParcel();
          this.campaignService.getCampaigns().subscribe((x) => {
            this.handleCampaigns(x);
          });
          this.farmService.getFarms().subscribe((x) => {
            this.handleFarms(x);
            this.getFarmParcel(this.parcel.farm.id, this.parcel.campaign.id);
          });

          this.getLists().subscribe((lists: Map<ListType, ListItem[]>) => {
            this.handleLists(lists);
          });
        }
      } else if (this.route.snapshot.url[1].path === 'edit') {
        if (params['parcelId'] !== undefined) {
          // edit
          this.parcelId = +params['parcelId'];
          this.edited = true;
          // va aussi récupérer les listes
          this.getParcel();
          // Check if crop can be edited for non-admin users
          this.checkCropEditPermission();
        }
      }
    });

    // this.getParcel();

    this.setupEsriGeocoding();

    if (this.i18nService.i18nInfo.country.hasRpg) {
      this.setupRpg();
    }

    // FIXME hack ? si pas ça, en combinaison avec setupEsriGeocoding, erreur sur le formulaire
    this.cdr.detectChanges();
  }

  onChangeFarm(farmId: any) {
    farmId = +farmId;
    this.parcel.farm.id = farmId;
    this.getFarmParcel(farmId, this.parcel.campaign.id);
  }

  private isUserLinkToFieldview() {
    this.secureLink.getFieldviewUser().subscribe((data: any) => {
      if (data && data.link == true) {
        this.fieldViewUserLink = true;
      }
    });
  }

  private getFarmParcel(farmId: number, campaignId: number) {
    if (!farmId) {
      return;
    }
    this.parcelService
      .getParcelsAdvanced(0, 100, 'id', '', {
        prefixParcel: '',
        prefixExploitation: '',
        prefixIdParcel: null,
        farmId: farmId,
        specyId: null,
        campaignId: campaignId,
        organisationId: null,
        noInvoice: null,
        advisorId: null,
      })
      .subscribe((parcels) => this.handleParcelsFarm(parcels));
  }

  private onChangeSpecy(specyId: any) {
    specyId = +specyId;
    this.parcel.variety = new Variety();
    this.parcel.variety.id = null;
    if (specyId) {
      const filteredDest = this.destinations.filter((dest) => dest.specy.id === specyId);
      if (filteredDest.length === 1) {
        this.parcel.destination = filteredDest[0];
      } else {
        this.parcel.destination = new Destination();
        this.parcel.destination.id = null;
      }

      const filtered = this.varieties.filter((variety) => variety.specy.id === specyId);
      if (filtered.length === 1) {
        this.parcel.variety = filtered[0];
      }
      this.updateFormForSpecy(specyId);
    }
  }

  private setupRpg() {
    const jsonLayer = new LayerJSON({
      url: API_ENDPOINT + '/rpg_parcels?bbox={lon1},{lat1},{lon2},{lat2}',
      minZoom: 15,
      minShift: 20,
      propertyItems: 'data',
      propertyTitle: 'id',
      propertyLoc: 'geometry.coordinates',
      caching: true,
      style: {
        color: '#ffff00',
        weight: 2,
        opacity: 0.7,
        fillOpacity: 0,
        fillColor: null,
      },
    });

    if (!this.controlLayer) {
      this.controlLayer = new L.Control.Layers({}, {}, { collapsed: false, position: 'bottomright' }).addTo(this.map);
    }

    this.controlLayer.addOverlay(jsonLayer, '<b>' + this.i18nTranslator.translate('parcel.edit.map.showRPG') + '</b>');

    const popup = new L.Popup();
    jsonLayer.on('click', (e: L.MouseEvent) => {
      //let bounds = e.sourceTarget.getBounds();
      const btn = this.createCreateFromRPGButton(
        this.i18nTranslator.translate('parcel.edit.map.contour'),
        'btn btn-primary',
      );
      const poly = new L.Polygon(e.sourceTarget.getLatLngs());
      L.DomEvent.on(btn, 'click', () => {
        this.chooseRPGParcelle(poly);
        if (popup && popup.isOpen()) {
          popup.remove();
        }
      });
      popup.setLatLng(e.latlng); //bounds.getCenter());
      popup.setContent(btn);
      this.map.openPopup(popup);
    });

    popup.on('add', (e: any) => {
      jsonLayer.unbindTooltip();
    });

    popup.on('remove', (e: any) => {
      this.bindTooltip(jsonLayer);
    });

    jsonLayer.on('remove', (e: any) => {
      if (popup && popup.isOpen()) {
        popup.remove();
      }
    });

    this.bindTooltip(jsonLayer);
  }

  private bindTooltip(jsonLayer: any) {
    jsonLayer.bindTooltip(this.i18nTranslator.translate('parcel.edit.map.selectContour'), { sticky: true });
  }

  private chooseRPGParcelle(polygon: L.Polygon) {
    const oldLayer = this.drawnItems.getLayers()[0];
    if (oldLayer) {
      this.drawnItems.removeLayer(oldLayer);
    }
    this.drawnItems.addLayer(polygon);
    this.map.fitBounds(polygon.getBounds());
  }

  private createCreateFromRPGButton(label: string, _class: string) {
    const btn = L.DomUtil.create('button', _class);
    btn.setAttribute('type', 'button');
    btn.innerHTML = label;
    return btn;
  }

  private updateFormForSpecy(specyId: number) {
    if (specyId) {
      for (const specy of this.species) {
        if (specy.id === specyId) {
          if (specy.code === 'VIGNE') {
            this.isShowPreviousCrop = false;
            this.isCerealDisabled = true;
            this.isFallowDisabled = true;
            this.parcel.previousCrop.id = null;
            this.parcel.cerealOutcome.id = null;
            this.parcel.fallowAge.id = null;
            this.parcel.fallowType.id = null;

            this.isShowSoilDepth = false;
            this.parcel.soilDepth.id = null;

            this.isShowSowedOn = false;
            this.parcel.sowedOn = null;
          } else {
            // si pas montré précdément
            // id inité à null
            this.isShowPreviousCrop = true;
            this.isShowSoilDepth = true;
            if (!this.parcel.sowedOn) {
              this.isSowedOnCollapsed = true;
            }
            this.isShowSowedOn = true;

            this.fillDefaultSoildDepthValues();
          }
          break;
        }
      }
    }
  }

  private onChangePreviousCrop(previousCropId: any) {
    previousCropId = +previousCropId;
    const previousCrop = this.previousCrops.find((x) => x.id === previousCropId);

    this.parcel.fallowAge.id = null;
    this.parcel.fallowType.id = null;
    this.parcel.cerealOutcome.id = null;

    if (previousCrop) {
      this.isFallowDisabled = !previousCrop.fallow;
      this.isCerealDisabled = !previousCrop.cereal;
    }
  }

  private setupEsriGeocoding() {
    const geocoderCodes = this.i18nService.i18nInfo.country.geocoderCodes;
    let countries: any = null;
    if (geocoderCodes) {
      countries = geocoderCodes.split(',');
    }
    const arcgisOnline = new L.esri.Geocoding.arcgisOnlineProvider({
      countries: countries,
      categories: ['Address', 'Postal', 'Populated Place', 'Coordinate System'],
      url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/',
    });

    const searchControl = L.esri.Geocoding.geosearch({
      position: 'topleft',
      useMapBounds: false,
      placeholder: this.i18nTranslator.translate('parcel.edit.map.searchAddress'),
      title: this.i18nTranslator.translate('parcel.edit.map.searchAddress'),
      allowMultipleResults: true,
      useArcgisWorldGeocoder: false,
      providers: [arcgisOnline],
    });
    searchControl.addTo(this.map);
  }

  private handleGeometry(layers: any) {
    if (!layers) {
      return;
    }

    const layer = layers.getLayers()[0];
    if (!layer) {
      return;
    }

    this.parcel.geometry = layer.toGeoJSON().geometry;
    this.parcel.area = this.mapComponent.area;
  }
}
